<template>
    <div class="payment-gateway-page-wrapper">
        <b-card class="template sidebar-header-card">
            <template #header>Payment Gateway</template>
            <b-card-body>
                <b-row>
                    <b-col v-if="!isEdit" md="12">
                        <div
                            v-if="paymentGatewayData.gateway"
                            @click="handleIsEdit(true)"
                            class="w-100 d-flex align-items-baseline cursor-pointer"
                        >
                            <h3 class="cursor-pointer text-primary mr-1">
                                {{ selectedGatewayLabel }}
                            </h3>
                            <font-awesome-icon class="text-primary" :icon="['fas', 'pencil-alt']" />
                        </div>
                    </b-col>
                    <b-col v-else md="4">
                        <form-dropdown
                            class="w-100"
                            :value="selectedGateway"
                            @input="onChangeGateway"
                            :options="gatewaysOptions"
                        ></form-dropdown>
                    </b-col>
                </b-row>
            </b-card-body>
        </b-card>
        <div class="pt-3 pb-3">
            <component
                :is="componentName"
                :initialValues="paymentGatewayData"
                :isEdit="isEdit"
                @cancel="onCancel"
                @submit="onSubmit"
                @edit="handleIsEdit(true)"
            />
        </div>
        <modal title="Warning" :id="confirmDisableGatewayModal" @ok.prevent="confirmDisable" @cancel="onCancel">
            {{ confirmDisableText[confirmDisableStep] }}
        </modal>
        <modal title="Warning" :id="confirmChangeGatewayModal" @ok.prevent="confirmChange" @cancel="onCancel">
            {{ confirmChangeText[confirmChangeStep] }}
        </modal>
        <modal title="Stripe Connect" :id="stripeMessageModal">
            {{ stripeMessage }}
        </modal>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { formatResponseError } from '../../../utils/api';
import { GATEWAYS, NONE_ID, PAYPAL_ID } from '../utils';
import FormDropdown from '../../generic/form-dropdown/form-dropdown.vue';
import AuthorizeEdit from './authorize-edit.vue';
import NoneEdit from './none-edit.vue';
import PaypalEdit from './paypal-edit.vue';
import StripeEdit from './stripe-edit.vue';
import QuantumEdit from './quantum-edit.vue';
import BeanstreamEdit from './beanstream-edit.vue';
import PayleapEdit from './payleap-edit.vue';
import Modal from '../../generic/modal/modal.vue';

export default {
    name: 'PaymentGatewaySetup',
    components: {
        FormDropdown,
        PaypalEdit,
        StripeEdit,
        NoneEdit,
        AuthorizeEdit,
        QuantumEdit,
        BeanstreamEdit,
        PayleapEdit,
        Modal,
    },
    data() {
        return {
            isEdit: false,
            selectedGateway: null,
            confirmChangeGatewayModal: 'confirm-change-gateway-id',
            confirmDisableGatewayModal: 'confirm-disable-gateway-id',
            confirmDisableStep: 0,
            confirmDisableText: [
                'Disabling your payment gateway is a BIG deal. ALL of your stored guest credit card data will be lost.',
                'We just want to double check with you. Are you absolutely sure you want to disable your payment gateway? All of your stored guest credit card data will be lost.',
            ],

            confirmChangeStep: 0,
            confirmChangeText: [
                'Changing your payment gateway is a BIG deal. ALL of your stored guest credit card data will be lost. Billing data is not transportable from one payment gateway to another.',
                'We just want to double check with you.  Are you absolutely sure you want to switch your payment gateway?  All of your stored guest credit card data will be lost.  There is no ability to transfer credit card data from one payment gateway to another.',
            ],
            values: null,
            stripeMessageModal: 'stripe-message-modal',
            stripeMessage: '',
        };
    },
    computed: {
        ...mapGetters('paymentGateway', ['paymentGatewayData', 'originalPaymentGatewayData']),
        gatewaysOptions() {
            return this.getPaymentGateways();
        },
        componentName() {
            return this.selectedGateway && this.selectedGateway + '-edit';
        },
        selectedGatewayLabel() {
            return this.selectedGateway && GATEWAYS.find((item) => item.value === this.selectedGateway)?.text;
        },
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        cloneDeep,
        async fetchData() {
            try {
                this.$parent.$emit('busy', true);
                const data = await this.$store.dispatch('paymentGateway/fetchPaymentGatewayData');
                this.selectedGateway = GATEWAYS.find((item) => item.key === data.gateway.id)?.value;
                if (data.gateway.id == NONE_ID) {
                    this.handleIsEdit(true);
                }
                if (data.stripe_connect_msg) {
                    this.stripeMessage = data.stripe_connect_msg;
                    this.$nextTick(() => {
                        this.$bvModal.show(this.stripeMessageModal);
                    });
                }
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch payment data' });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        handleIsEdit(value) {
            this.isEdit = value;
        },
        getPaymentGateways() {
            if (this.paymentGatewayData.only_paypal) {
                return [...GATEWAYS].filter((item) => item.id === PAYPAL_ID);
            }
            const excludedGateways = ['beanstream', 'quantum', 'payleap'];
            const gateways = [...GATEWAYS].filter((item) => {
                if (
                    excludedGateways.includes(item.value.toLowerCase()) &&
                    excludedGateways.includes(this.paymentGatewayData?.gateway?.name?.toLowerCase())
                ) {
                    return item.key === this.paymentGatewayData?.gateway?.id;
                } else {
                    return !excludedGateways.includes(item.value);
                }
            });
            return gateways;
        },
        onChangeGateway(value) {
            this.selectedGateway = value;
            const gatewayId = GATEWAYS.find((item) => item.value === this.selectedGateway).key;
            const values = this.cloneDeep(this.paymentGatewayData);
            this.$store.commit('paymentGateway/gotPaymentGatewayData', {
                ...values,
                gateway: {
                    ...values.gateway,
                    name: this.selectedGatewayLabel,
                    id: gatewayId,
                },
            });
        },
        onCancel() {
            this.confirmDisableStep = 0;
            this.confirmChangeStep = 0;
            this.$store.commit('paymentGateway/gotPaymentGatewayData', this.cloneDeep(this.originalPaymentGatewayData));
            this.selectedGateway = GATEWAYS.find(
                (item) => item.key === this.originalPaymentGatewayData.gateway.id
            )?.value;
            this.handleIsEdit(false);
        },
        isChangedImportantFields() {
            const importantFields = GATEWAYS.find((item) => this.paymentGatewayData.gateway.id === item.key)?.fields;
            let changed = false;
            if (importantFields) {
                importantFields.forEach((item) => {
                    changed = this.paymentGatewayData.gateway[item] !== this.originalPaymentGatewayData.gateway[item];
                });
            }
            return changed;
        },
        confirmDisable() {
            if (this.confirmDisableStep) {
                this.save(this.confirmDisableGatewayModal);
            } else {
                this.confirmDisableStep += 1;
            }
        },
        confirmChange() {
            if (this.confirmChangeStep) {
                this.save(this.confirmChangeGatewayModal);
            } else {
                this.confirmChangeStep += 1;
            }
        },
        async save(modalId) {
            try {
                const values = this.paymentGatewayData.gateway;
                this.$parent.$emit('busy', true);
                await this.$store.dispatch('paymentGateway/savePaymentGatewayData', values);
                this.successToast({ text: 'Data has been successfully saved' });
                if (modalId) {
                    this.$bvModal.hide(modalId);
                }
                this.confirmDisableStep = 0;
                this.confirmChangeStep = 0;
                this.handleIsEdit(false);
                this.fetchData();
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not save data' });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        onSubmit(values) {
            this.$store.commit('paymentGateway/gotPaymentGatewayData', this.cloneDeep(values));
            const changed = this.isChangedImportantFields();
            if (this.originalPaymentGatewayData.gateway.id === NONE_ID) {
                this.save();
            } else if (this.selectedGateway === 'none') {
                this.$bvModal.show(this.confirmDisableGatewayModal);
            } else if (this.selectedGateway !== this.originalPaymentGatewayData.gateway.name.toLowerCase() || changed) {
                this.$bvModal.show(this.confirmChangeGatewayModal);
            } else {
                this.save();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../styles.scss';
</style>